/**
 * Created by Ella Ma on 2020/09/11.
 * ------ maintenance history ------
 * Updated by Daniel Wang on 6/2/2023, added creating sidenote
 */

import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { AutomationModelBase } from '../../tamalelibs/models/automation-base.model';
import { DiagramAutomationNoteDialogService } from './diagram-automation-dialog/diagram-note-automation-dialog/diagram-note-automation-dialog.service';
import { DiagramRelationshipDialogService } from './diagram-automation-dialog/diagram-relationship-automation-dialog/diagram-relationship-automation-dialog.service';
import { DiagramConditionalGatewayDialogService } from './diagram-conditional-gateway-dialog/diagram-conditional-gateway-dialog.service';
import { DiagramContextMenuEventType, DiagramContextMenu, DiagramContextMenuActionType, AutomationActionType } from './diagram-context-menu/diagram-context-menu.model';
import { DiagramConfig, NodeActionType, NodeEventType, START_NODE } from './diagram.model';
import { DiagramService } from './diagram.service';

@Component({
    selector: 'tam-diagram',
    templateUrl: './diagram.component.html',
    styleUrls: ['./diagram.component.scss'],
    providers: [DiagramConditionalGatewayDialogService, DiagramAutomationNoteDialogService, DiagramRelationshipDialogService]
})
export class DiagramComponent implements OnInit, OnDestroy {

    @Input() config: DiagramConfig;

    nodes;
    diagramContextMenu = new DiagramContextMenu();
    diagramNodeWidth = 0;

    private _destroySubscriptions: Array<Subscription> = [];
    private _diagramNodeContainerEl;

    constructor(
        private _elementRef: ElementRef,
        private _r2: Renderer2,
        private _nodesService: DiagramService,
        private _automationNoteDialogService: DiagramAutomationNoteDialogService,
        private _automationRelationDialogService: DiagramRelationshipDialogService,
    ) { }

    ngOnInit() {
        this._nodesService.setDiagramConfig(this.config);
        this._initNodes();
        this._diagramNodeContainerEl = this._elementRef.nativeElement.querySelector('.container');
        const handleDialogResultFunc = (isConfirmed: boolean, result: AutomationModelBase, id: string, isUpdate: boolean) => {
            if (isConfirmed && result) {
                if (!isUpdate) {
                    this._nodesService.appendAutomation(result, id);
                } else {
                    this._nodesService.updateAutomation(result, id);
                }
            }
        };
        this._destroySubscriptions.push(
            this.diagramContextMenu.events$.subscribe((event) => {
                switch (event.type) {
                    case DiagramContextMenuEventType.nodeAdd: {
                        this._nodesService.newNode({
                            type: event.payload.nodeType,
                            targetId: event.payload.targetId || START_NODE.guid,
                            condition: event.payload.condition,
                            diagramContextMenu: this.diagramContextMenu,
                            parentBranch: event.payload.parentBranch,
                        });
                        break;
                    }
                    case DiagramContextMenuEventType.nodeMove: {
                        this._nodesService.moveNode({
                            targetId: event.payload.targetId || START_NODE.guid,
                            condition: event.payload.condition,
                            diagramContextMenu: this.diagramContextMenu,
                            moveTask: event.payload.moveTask
                        });
                        break;
                    }
                    // move automation node
                    case DiagramContextMenuEventType.automationMove: {
                        this._nodesService.moveAutomation({
                            targetId: event.payload.targetId || START_NODE.guid,
                            condition: event.payload.condition,
                            diagramContextMenu: this.diagramContextMenu,
                            moveAutomation: event.payload.moveAutomation
                        });
                        break;
                    }
                    case DiagramContextMenuEventType.resetWidth: {
                        this._calculateNodeWrapWidth();
                        break;
                    }
                    case DiagramContextMenuEventType.automationAdd: {
                        if (event.payload.actionType === AutomationActionType.CreateANote) {
                            this._automationNoteDialogService.dialogOpen$.next({
                                nodeId: event.payload.targetId,
                                isUpdate: false,
                                taskArray: this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id })),
                                type: AutomationActionType.CreateANote // set type for creating a note
                            });
                        } else if (event.payload.actionType === AutomationActionType.CreateASidenote) {
                            this._automationNoteDialogService.dialogOpen$.next({
                                nodeId: event.payload.targetId,
                                isUpdate: false,
                                taskArray: this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id })),
                                type: AutomationActionType.CreateASidenote // set type for creating a sidenote
                            });
                        } else if (event.payload.actionType === AutomationActionType.UpdateANote) {
                            this._automationNoteDialogService.dialogOpen$.next({
                                nodeId: event.payload.targetId,
                                isUpdate: false,
                                taskArray: this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id })),
                                type: AutomationActionType.UpdateANote // set type for updating a note
                            });
                        } else if (event.payload.actionType === AutomationActionType.CreateRelationships) {
                            this._automationRelationDialogService.dialogOpen$.next({
                                nodeId: event.payload.targetId,
                                isUpdate: false,
                                taskArray: this._nodesService.getAllTaskNodes().map(node => ({ key: node.name, value: node.id }))
                            });
                        }
                        break;
                    }
                    default:
                        break;
                }
            }),
            this.config.actions$.subscribe(action => {
                if (action.type === NodeActionType.refresh) {
                    this.refresh();
                } else if (action.type === NodeActionType.closeContextMenu) {
                    this.diagramContextMenu.actions$.next({
                        type: DiagramContextMenuActionType.CloseMenu,
                    });
                }
            }),
            this._automationNoteDialogService.dialogClose$.subscribe(dialogResult => {
                handleDialogResultFunc(dialogResult.isConfirmed, dialogResult.mappingResult, dialogResult.nodeId, dialogResult.isUpdate);
            }),
            this._automationRelationDialogService.dialogClose$.subscribe(dialogResult => {
                handleDialogResultFunc(dialogResult.isConfirmed, dialogResult.relationResult, dialogResult.nodeId, dialogResult.isUpdate);
            })
        );
        this._calculateNodeWrapWidth();
    }

    ngOnDestroy() {
        this._nodesService.destory();
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onStart() {
        this.config.events$.next({
            type: NodeEventType.clickNode,
            payload: START_NODE
        });
    }

    showTaskActionContextMenu(event, el) {
        event.stopPropagation();
        // no condition and targetId for the first node
        this.diagramContextMenu.actions$.next({
            type: DiagramContextMenuActionType.LoadData,
            payload: {
                event: event,
                anchor: el
            }
        });
    }

    refresh() {
        this.ngOnDestroy();
        this.ngOnInit();
    }

    /**
     * when add some branch node and input node, the container will show scrollbar, so it's width will be added, the function to align center all nodes
     * node-wrap is text-node, start-node, end-node
     * branch-wrap is branch-node
     * only node width >= page.clientWidth, when page.clientWidth chagne, node width change, if node width < page.clientWidth, page.clientWidth change, node width do not change
     */
    private _calculateNodeWrapWidth() {
        setTimeout(() => {
            const arr = [];
            this._elementRef.nativeElement.querySelectorAll('.branch-box').forEach(element => {
                arr.push(element.clientWidth);
            });
            const scrollWidth = this._diagramNodeContainerEl.scrollWidth;
            const contentWidth = Math.max.apply(null, arr);
            const clientWidth = this._diagramNodeContainerEl.clientWidth;
            const diagramNodeEl = this._elementRef.nativeElement.querySelectorAll('.node-wrap');
            const diagramBranchNodeEl = this._elementRef.nativeElement.querySelectorAll('.branch-wrap');
            if (contentWidth > clientWidth) {
                diagramNodeEl.forEach(element => {
                    if (element.clientWidth >= clientWidth) {
                        this._r2.setStyle(element, 'width', contentWidth + 'px');
                    } else {
                        this._r2.setStyle(element, 'width', '100%');
                    }
                });
                diagramBranchNodeEl.forEach(element => {
                    if (element.clientWidth >= clientWidth) {
                        this._r2.setStyle(element, 'width', contentWidth + 'px');
                    } else {
                        this._r2.setStyle(element, 'width', '100%');
                    }
                });
                this.diagramNodeWidth = contentWidth;
            } else {
                diagramNodeEl.forEach(element => {
                    this._r2.setStyle(element, 'width', '100%');
                });
                diagramBranchNodeEl.forEach(element => {
                    this._r2.setStyle(element, 'width', '100%');
                });
            }
        }, 0);

    }

    private _initNodes() {
        this.config.nodes.forEach((node) => {
            node.diagramContextMenu = this.diagramContextMenu;
        });
        this.nodes = this._nodesService.loadNodes(this.config.nodes);
    }
}
